import React from "react";
import { useScrollOnTabChange } from "../../../hooks/useScrollOnTabChange";

export type DashboardTabItem<T extends string> = {
  id: T;
  label: string;
  badge?: React.ReactNode;
  disabled?: boolean;
};

type DashboardTabState = {
  active: boolean;
  disabled: boolean;
};

type DashboardTabsProps<T extends string> = {
  activeTab: T;
  ariaLabel: string;
  barClassName?: string;
  navClassName?: string;
  getTabClassName?: (state: DashboardTabState) => string;
  items: ReadonlyArray<DashboardTabItem<T>>;
  onTabChange: (tabId: T) => void;
};

const DashboardTabs = <T extends string>({
  activeTab,
  ariaLabel,
  barClassName = "overflow-x-auto border-b border-gray-200 bg-white md:-mx-8",
  navClassName = "flex min-w-max items-end justify-start gap-10 px-4 sm:px-8",
  getTabClassName,
  items,
  onTabChange,
}: DashboardTabsProps<T>) => {
  useScrollOnTabChange(activeTab);
  return (
    <div className={barClassName}>
      <nav
        className={navClassName}
        aria-label={ariaLabel}
      >
        {items.map((tab) => {
          const active = activeTab === tab.id;
          const disabled = tab.disabled ?? false;
          const className = getTabClassName
            ? `whitespace-nowrap transition focus:outline-none focus-visible:ring-2 focus-visible:ring-cpaGold/30 ${getTabClassName({ active, disabled })}`
            : `whitespace-nowrap border-b-2 px-1 py-4 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-cpaGold/30 ${
                active
                  ? "border-cpaGold text-cpaGold"
                  : "border-transparent text-gray-950 hover:border-gray-300 hover:text-black"
              }`;

          return (
            <button
              key={tab.id}
              onClick={() => {
                if (!disabled) {
                  onTabChange(tab.id);
                }
              }}
              className={`${className}${disabled ? " cursor-default" : ""}`}
              aria-current={active ? "page" : undefined}
              aria-disabled={disabled || undefined}
              aria-label={`${tab.label} tab`}
              tabIndex={disabled ? -1 : undefined}
              type="button"
            >
              {tab.label}
              {tab.badge}
            </button>
          );
        })}
      </nav>
    </div>
  );
};

export default DashboardTabs;
